import { SvgIconComponent } from '@mui/icons-material';
import { Box, Paper, SxProps, Theme, Typography } from '@mui/material';
import { BASE_COLORS } from 'lib/functions/const/colors';
import React from 'react';

import { hexToRGBA } from '../BarChart';

export interface AlertBannerProps {
  severity?: 'error' | 'warning' | 'info' | 'success';
  title?: string;
  message?: string;
  description?: string;
  icon?: React.ReactElement<SvgIconComponent>;
  sx?: SxProps<Theme>;
  showTitle?: boolean;
  showIcon?: boolean;
  customIcon?: React.ReactElement;
}

const AlertBanner: React.FC<AlertBannerProps> = ({
  severity = 'warning',
  title,
  message,
  description,
  icon,
  sx = {},
  showTitle = true,
  showIcon = true,
  customIcon,
}) => {
  const severityStyles: Record<
    string,
    { color: string; borderColor: string; bgColor: string }
  > = {
    error: {
      color: BASE_COLORS.red,
      borderColor: hexToRGBA(BASE_COLORS.red, 0.15),
      bgColor: hexToRGBA(BASE_COLORS.red, 0.04),
    },
    warning: {
      color: BASE_COLORS.yellow,
      borderColor: hexToRGBA(BASE_COLORS.yellow, 0.2),
      bgColor: hexToRGBA(BASE_COLORS.yellow, 0.04),
    },
    info: {
      color: BASE_COLORS.blue,
      borderColor: hexToRGBA(BASE_COLORS.blue, 0.15),
      bgColor: hexToRGBA(BASE_COLORS.blue, 0.04),
    },
    success: {
      color: BASE_COLORS.green,
      borderColor: hexToRGBA(BASE_COLORS.green, 0.15),
      bgColor: hexToRGBA(BASE_COLORS.green, 0.04),
    },
  };

  const currentStyle = severityStyles[severity];
  const displayIcon = showIcon ? customIcon || icon : null;

  const defaultSx: SxProps<Theme> = {
    my: 2,
    p: 2,
    borderRadius: '8px',
    boxShadow: 'none',
    border: `1px solid ${currentStyle.borderColor}`,
    backgroundColor: currentStyle.bgColor,
  };

  const mergedSx = {
    ...defaultSx,
    ...sx,
  };

  return (
    <Paper sx={mergedSx} elevation={0}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        {displayIcon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '20px',
              mt: '2px',
              color: currentStyle.color,
              '& svg': {
                fontSize: '18px',
                color: currentStyle.color,
              },
            }}
          >
            {displayIcon}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {showTitle && title && (
            <Typography
              sx={{
                fontWeight: 500,
                fontSize: '1rem',
                color: currentStyle.color,
                marginBottom: message || description ? '4px' : 0,
                lineHeight: 1.4,
              }}
            >
              {title}
            </Typography>
          )}

          {message && (
            <Typography
              sx={{
                color: 'text.primary',
                mb: description ? 0.75 : 0,
                fontSize: '0.875rem',
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              {message}
            </Typography>
          )}

          {description && (
            <Typography
              sx={{
                fontSize: '0.8125rem',
                color: 'text.secondary',
                fontStyle: 'italic',
                fontWeight: 300,
                lineHeight: 1.5,
                mt: message ? 0.75 : 0,
              }}
            >
              {description}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default AlertBanner;
